import { Avatar, AvatarFallback, AvatarImage } from '@kit/ui/avatar';
import { cn } from '@kit/ui/utils';

/**
 * Canonical organization avatar: renders the resolved logo when present,
 * otherwise up to two uppercase initials derived from the org name. The
 * single org-avatar surface — pair it with a URL from `resolveOrgMediaUrls`
 * (never a raw `logo_media_id`, and never a phantom `logo_url` column).
 */
export function OrganizationAvatar({
  name,
  logoUrl,
  className,
  fallbackClassName,
}: {
  name: string;
  logoUrl?: string | null;
  className?: string;
  fallbackClassName?: string;
}) {
  return (
    <Avatar className={cn('h-9 w-9 rounded-md', className)}>
      {logoUrl ? <AvatarImage src={logoUrl} alt={name} /> : null}
      <AvatarFallback
        className={cn(
          'bg-muted text-muted-foreground rounded-md text-xs font-semibold',
          fallbackClassName,
        )}
      >
        {getOrganizationInitials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

/** First letters of the first two words, uppercased (e.g. "Central School" → "CS"). */
function getOrganizationInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const initials = words.slice(0, 2).map((w) => w[0] ?? '');
  return initials.join('').toUpperCase();
}
